import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../../components/ui/tabs';
import '../../../../styles/map.css';
import { ProjectTeam } from './team/ProjectTeam';
import { ProjectMaterials } from './materials/ProjectMaterials';
import { ProjectOverview } from './overview/ProjectOverview';
import { ProjectSetup } from './setup/ProjectSetup';
import { ProjectSpecifications } from './specifications/ProjectSpecifications';
import { ProjectReports } from './reports/ProjectReports';
import type { DashboardProject } from '../../types/project';
import { Pencil, ChevronLeft, ChevronRight, LayoutDashboard, Settings, FileText, Package, Users, BarChart2, LineChart, Hammer, AlertTriangle, Wrench, ScrollText, Mail } from 'lucide-react';
import { ProjectPipefitter } from '../../../pipefitter/ProjectPipefitter';
import { ProjectProduction } from './production/ProjectProduction';
import { AssemblyProduction } from './production/AssemblyProduction';
import { EquipmentAndConsumableRegistry } from './equipment/EquipmentAndConsumableRegistry';
import { ARKRegistryDocumentTable } from './equipment/ARKRegistryDocumentTable';
import { ProjectEmailConfiguration } from './emailConfig/ProjectEmailConfiguration';
import { Notification as customNotification } from '../../../../components/ui/custom-notification';
import { ConfirmationDialog } from '../../../../components/ui/ConfirmationDialog';
import { Button } from '../../../../components/ui/button';
import { canViewTab, canEditTab, canReportTab, type TeamMemberPermissions } from '../../utils/permissions';
import { useUserPermissionsAfterLogin } from '../../../admin/hooks/useUserPermissionsAfterLogin';
import { getAllPipeDetails, getListManufacturingCategory, clearAllPipeDetails } from '../../../../redux/Project/setupData/action';
import { useDispatch, useSelector } from 'react-redux';
import { AnalyticsShell } from './AnalyticsShell';
import { AuditTrailView } from './auditTrail/AuditTrailView';
import { logActivityTabView } from '../../../audit/activityAudit';

interface ProjectDashboardProps {
  project: DashboardProject;
  onBack: () => void;
  urlTab?: string;
}

const DashedBoard: React.FC<ProjectDashboardProps> = ({ project, onBack, urlTab }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>(urlTab || 'overview');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [productionViewMode, setProductionViewMode] = useState<'table' | 'map'>('table');

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [pendingTabChange, setPendingTabChange] = useState<string | null>(null);

  // Get user role from useUserPermissionsAfterLogin
  const { userRole, hasPermission } = useUserPermissionsAfterLogin();

  // Get team member permissions from project (project object may have team_member_permissions)
  const teamMemberPermissions = useMemo(() => {
    // Check if project has team_member_permissions (it might be on project object or project.project)
    return (project as any)?.team_member_permissions || (project.project as any)?.team_member_permissions;
  }, [project]);

  // Redux hooks
  const dispatch = useDispatch();
  const { pipeDetails, isPipeDetailsLoading } = useSelector((state: any) => state.setupData || {});
  
  // Track if pipeDetails has been fetched for current project ID
  const fetchedProjectIdRef = useRef<string | number | null>(null);
  const isFetchingPipeDetailsRef = useRef(false);
let alrealaodDataRef = false;
  // Load initial data - only fetch once per project ID
  useEffect(() => {
    
    const currentProjectId = project.project.projectId;
    
    // Skip if no project ID
    if (!currentProjectId) {
      return;
    }
    
    // Skip if already fetched for this project ID
    if (fetchedProjectIdRef.current === currentProjectId) {
      return;
    }
    
    // Skip if already fetching
    if (isPipeDetailsLoading || isFetchingPipeDetailsRef.current) {
      return;
    }
    if(alrealaodDataRef) {
      console.log('already loaded data');
      return;
    }
    // Fetch pipeDetails only once for this project ID
    isFetchingPipeDetailsRef.current = true;
    fetchedProjectIdRef.current = currentProjectId;
    alrealaodDataRef = true;
    dispatch(clearAllPipeDetails());
    dispatch(getAllPipeDetails({ projectId: currentProjectId }));
    dispatch(getListManufacturingCategory({}));
      //     }
    // Reset fetching flag after a short delay to allow Redux state to update
    setTimeout(() => {
      isFetchingPipeDetailsRef.current = false;
      alrealaodDataRef = false;
    }, 200);
  }, [project.project.projectId, dispatch, isPipeDetailsLoading]);

  

  // Simple function to check if any pipes are in active state (unsaved changes)
  const OnChanged = (hasUnsavedChanges: boolean): void => {
    // For now, return false as a simple implementation
    // This can be enhanced based on your actual state management
    setHasUnsavedChanges(hasUnsavedChanges);
  };

  const TABS_CONFIG = useMemo(() => ({
    overview: {
      label: 'Overview',
      icon: LayoutDashboard,
      content: () => <div><ProjectOverview project={project.project} /></div>
    },
    setup: {
      label: 'Setup',
      icon: Settings,
      content: () => <div><ProjectSetup project={project} OnChanged={OnChanged} canEdit={canEditTab(teamMemberPermissions, 'setup', userRole)} /></div>
    },
    specifications: {
      label: 'Specifications',
      icon: FileText,
      content: () => <div><ProjectSpecifications project={project} canEdit={canEditTab(teamMemberPermissions, 'specifications', userRole)} canReport={canReportTab(teamMemberPermissions, 'specifications', userRole)} /></div>
    },
    'material-traceability': {
      label: 'Material Traceability',
      icon: Package,
      content: () => <div><ProjectMaterials project={project} canEdit={canEditTab(teamMemberPermissions, 'material-traceability', userRole)} canReport={canReportTab(teamMemberPermissions, 'material-traceability', userRole)} /></div>
    },
    pipefitter: {
      label: 'Pipefitter',
      icon: Wrench,
      content: () => <div><ProjectPipefitter project={project} canEdit={canEditTab(teamMemberPermissions, 'pipefitter', userRole)} canReport={canReportTab(teamMemberPermissions, 'pipefitter', userRole)} /></div>
    },
    production: {
      label: 'Production',
      icon: Hammer,
      content: () => <div><ProjectProduction project={project} onViewModeChange={setProductionViewMode} canEdit={canEditTab(teamMemberPermissions, 'production', userRole)} canReport={canReportTab(teamMemberPermissions, 'production', userRole)} /></div>
    },
    'team-members': {
      label: 'Team Members',
      icon: Users,
      content: () => <div><ProjectTeam project={project} canEdit={canEditTab(teamMemberPermissions, 'team-members', userRole)} canReport={canReportTab(teamMemberPermissions, 'team-members', userRole)} /></div>
    },
    'email-configuration': {
      label: 'Email Configuration',
      icon: Mail,
      content: () => (
        <div>
          <ProjectEmailConfiguration
            project={project}
            canEdit={canEditTab(teamMemberPermissions, 'email-configuration', userRole)}
          />
        </div>
      ),
    },

    analytics: {
      label: 'Analytics',
      icon: LineChart,
      content: () => <div><AnalyticsShell project={project} /></div>
    },
    'equipment-and-consumable-registry': {
      label: 'Registry',
      icon: Wrench,
      content: () => <div><EquipmentAndConsumableRegistry project={project}  canEdit={canEditTab(teamMemberPermissions, 'equipment-and-consumable-registry', userRole)} canReport={canReportTab(teamMemberPermissions, 'equipment-and-consumable-registry', userRole)}/></div>
    },
    'Other Documents': {
      label: 'Other Documents',
      icon: Wrench,
      content: () => <div><ARKRegistryDocumentTable projectId={project?.project?.projectId ? Number(project.project.projectId) : 0}
        projectName={project?.project?.projectName}
        canEdit={canEditTab(teamMemberPermissions, 'Other Documents', userRole)}
       /></div>
    },

    reports: {
      label: 'Reports',
      icon: BarChart2,
      content: () => <div><ProjectReports project={project} /></div>
    },
    'audit-trail': {
      label: 'Audit Trail',
      icon: ScrollText,
      content: () => (
        <AuditTrailView
          projectId={
            project?.project?.projectId != null
              ? Number(project.project.projectId)
              : undefined
          }
          projectName={project?.project?.projectName ?? null}
          description="Project activity log for compliance and troubleshooting. Filter by module, event, actor, or date range."
        />
      ),
    },


  } as const), [project, teamMemberPermissions, userRole]);

  // Optional: show Pipefitter tab only when project has pipeFitter feature enabled
  const isPipefitterEnabledForProject = useMemo(() => {
    const pt = project?.project?.projectTypes;
    if (!pt) return false;
    if (pt.pipeline?.features?.pipeFitter?.enabled) return true;
    if (pt.others?.features?.pipeFitter?.enabled) return true;
    const fab = pt.fabrication?.options;
    if (fab?.buckleArrestor?.features?.pipeFitter?.enabled) return true;
    if (fab?.pipeInPipe?.features?.pipeFitter?.enabled) return true;
    if (fab?.bulkhead?.features?.pipeFitter?.enabled) return true;
    return false;
  }, [project]);

  // Get visible tabs
  const visibleTabs = useMemo(() => {
    return Object.keys(TABS_CONFIG).filter(key => {
      if (key === 'audit-trail') {
        return hasPermission('view_audit_logs');
      }
      if (!canViewTab(teamMemberPermissions, key, userRole)) return false;
      if (key === 'pipefitter' && !isPipefitterEnabledForProject) return false;
      return true;
    });
  }, [TABS_CONFIG, teamMemberPermissions, userRole, isPipefitterEnabledForProject, hasPermission]);

  // Keep activeTab in sync with URL :tab param (drives refresh persistence).
  useEffect(() => {
    const desired = urlTab && visibleTabs.includes(urlTab) ? urlTab : (urlTab ? activeTab : 'overview');
    if (desired !== activeTab) setActiveTab(desired);
  }, [urlTab, visibleTabs]); // eslint-disable-line react-hooks/exhaustive-deps

  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollButtons = useCallback(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    setCanScrollLeft(scrollLeft > 1);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
  }, []);

  useEffect(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    updateScrollButtons();
    const onScroll = () => updateScrollButtons();
    el.addEventListener('scroll', onScroll, { passive: true });
    const ro = new ResizeObserver(() => updateScrollButtons());
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, [visibleTabs, updateScrollButtons]);

  const handleScrollLeft = () => {
    tabsContainerRef.current?.scrollBy({ left: -160, behavior: 'smooth' });
  };

  const handleScrollRight = () => {
    tabsContainerRef.current?.scrollBy({ left: 160, behavior: 'smooth' });
  };

  // If active tab is not visible, redirect to the first visible tab.
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.includes(activeTab)) {
      const fallback = visibleTabs[0];
      setActiveTab(fallback);
      navigate(`/Projects/${project.project.projectId}/${fallback}`, { replace: true });
    }
  }, [visibleTabs, activeTab, navigate, project.project.projectId]);

  const navigateToTab = (value: string) => {
    const tabLabel = TABS_CONFIG[value as keyof typeof TABS_CONFIG]?.label ?? value;
    logActivityTabView(
      tabLabel,
      project.project.projectId,
      project.project.projectName
    );
    navigate(`/Projects/${project.project.projectId}/${value}`);
  };

  // Ensure active tab is scrolled into view when selected
  useEffect(() => {
    const container = tabsContainerRef.current;
    if (!container) {
      return;
    }

    const activeTrigger = container.querySelector<HTMLElement>('[data-state="active"]');
    if (activeTrigger) {
      const { offsetLeft, offsetWidth } = activeTrigger;
      const { scrollLeft, clientWidth } = container;

      const isFullyVisible =
        offsetLeft >= scrollLeft &&
        offsetLeft + offsetWidth <= scrollLeft + clientWidth;

      if (!isFullyVisible) {
        activeTrigger.scrollIntoView({
          behavior: 'smooth',
          inline: 'center',
          block: 'nearest',
        });
      }

      // Recalculate arrow visibility after auto-scrolling
      updateScrollButtons();
    }
  }, [activeTab, visibleTabs.length, updateScrollButtons]);

  const onActiveTabChange = (value: string) => {
    if (activeTab === 'setup' && value !== 'setup') {
      if (hasUnsavedChanges) {
        setPendingTabChange(value);
        setIsConfirmDialogOpen(true);
        return;
      }
    }
    navigateToTab(value);
  }

  const handleConfirmTabChange = () => {
    if (pendingTabChange) {
      navigateToTab(pendingTabChange);
      setPendingTabChange(null);
    }
    setHasUnsavedChanges(false);
    OnChanged(false);
    setIsConfirmDialogOpen(false);
  }

  const handleCancelTabChange = () => {
    setPendingTabChange(null);
    setIsConfirmDialogOpen(false);
    
  }

  return (
    <div className="pt-2 pb-0 flex-1 flex flex-col min-h-0">
      <div className="mx-auto px-2 flex-1 flex flex-col w-full min-h-0">
        <div className="bg-white rounded-lg shadow-sm flex-1 flex flex-col min-h-0">

              <div className={`bg-white rounded-[0.5rem] shadow-sm flex flex-col flex-1 min-h-0 ${activeTab === 'production' && productionViewMode === 'map' ? 'map-view-override min-h-[calc(100vh-100px)] overflow-hidden' : ''}`}>
                <Tabs value={activeTab} onValueChange={(value)=>onActiveTabChange(value)} className="w-full h-full flex flex-col ">
                  <div className="sticky top-0 bg-white rounded-t-lg z-30 border-b border-[#E2E5EA]">
                    <div className="flex items-center gap-1 bg-transparent px-2 md:px-6 pt-2 md:pt-3 pb-0">
                      {canScrollLeft && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 text-heritage-grey hover:text-blueprint-blue mb-2"
                          onClick={handleScrollLeft}
                          aria-label="Scroll tabs left"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </Button>
                      )}
                      <div
                        ref={tabsContainerRef}
                        className="flex-1 min-w-0 overflow-x-auto overflow-y-hidden whitespace-nowrap horizontal-tabs-scroll"
                      >
                        <TabsList className="h-auto w-max justify-start gap-4 md:gap-6 lg:gap-8 bg-transparent">
                          {visibleTabs.map((key) => {
                            const { label, icon: Icon } = TABS_CONFIG[key as keyof typeof TABS_CONFIG];
                            return (
                              <TabsTrigger
                                key={key}
                                value={key}
                                className="relative inline-flex flex-shrink-0 items-center gap-1.5 rounded-none border-b-[3px] border-transparent px-0.5 pb-3 pt-1.5 text-sm font-medium text-heritage-grey hover:text-blueprint-blue whitespace-nowrap data-[state=active]:border-hero-red data-[state=active]:text-hero-red focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
                              >
                                <Icon className="h-3.5 w-3.5" strokeWidth={1.75} />
                                <span className="hidden sm:inline">{label}</span>
                                <span className="sm:hidden">{label.split(' ')[0]}</span>
                              </TabsTrigger>
                            );
                          })}
                        </TabsList>
                      </div>
                      {canScrollRight && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="shrink-0 text-heritage-grey hover:text-blueprint-blue mb-2"
                          onClick={handleScrollRight}
                          aria-label="Scroll tabs right"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
                    {visibleTabs.map((key) => {
                      const Content = TABS_CONFIG[key as keyof typeof TABS_CONFIG].content;
                      return (
                      <TabsContent
                        key={key}
                        value={key}
                        className={`h-full flex-1 min-h-0 min-w-0 overflow-auto ${key === 'production' && productionViewMode === 'map' ? 'tabs-content' : ''}`}
                      >
                        <div className="min-h-full w-full max-w-full px-4 py-2">
                          <Content />
                        </div>
                      </TabsContent>
                    );})}
                  </div>
                </Tabs>
              </div>
          
        </div>
      </div>
        <ConfirmationDialog
          open={isConfirmDialogOpen}
          onOpenChange={setIsConfirmDialogOpen}
          title="Unsaved Changes"
          message="Unsaved changes detected in the Setup tab. Switching to another tab will discard these changes. Do you want to continue?"
          note={<><span className="font-semibold">Note:</span> This action is irreversible after switching tabs.</>}
          confirmLabel="Switch Tab"
          onConfirm={handleConfirmTabChange}
          onCancel={handleCancelTabChange}
        />
    </div>
  );
};

export default DashedBoard;